/**
 * Voice streaming event handlers — contentStart, textOutput, audioOutput,
 * contentEnd, streamComplete, and error with auto-restart logic.
 */

import type { Socket } from "socket.io-client";
import {
  type ServerToClientEvents,
  type ClientToServerEvents,
  classifyVoiceError,
} from "@amzn/a4k-creator-studio-shared";
import { isVoiceLocked, cancelPendingAutoRestart, setAutoRestartTimer, getVoiceGeneration, clearStopGate, isSuppressed, clearMuteTimer, useSocketStore } from "../socketStore";
import { useStoryStore } from "../storyStore";
import { useAudioStore } from "../audioStore";
import { useChatStore } from "../chatStore";
import { useErrorStore } from "../errorStore";
import { VOICE_ERROR } from "../errorMessages";
import { useFaceStore } from "../faceStore";
import { decodeBase64Audio } from "../../audio/audioUtils";
import minervaService from "../../services/minervaService";
import { AppEvent } from "../../services/appEvents";
import { trackMuseLatency, trackErrorToast, Muse } from "../../services/metrics";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface VoiceStreamState {
  currentContentRole: string | null;
  userTranscriptBuffer: string;
  userSpeechStartMs: number;
  userSpeechEndMs: number;
  assistantEndMs: number;
  awaitingFirstResponse: boolean;
}

export function registerVoiceStreamHandlers(
  socket: TypedSocket,
  get: () => { isVoiceActive: boolean; isConnected: boolean; socket: TypedSocket | null },
  set: (partial: Partial<{ transcriptText: string; isVoiceActive: boolean }>) => void,
): void {
  const vs: VoiceStreamState = {
    currentContentRole: null,
    userTranscriptBuffer: "",
    userSpeechStartMs: 0,
    userSpeechEndMs: 0,
    assistantEndMs: 0,
    awaitingFirstResponse: false,
  };

  /** Consecutive error-restart attempts. Reset on successful audio output.
   *  Prevents infinite restart loops from persistent Bedrock connection issues. */
  let errorRestartCount = 0;
  const MAX_ERROR_RESTARTS = 3;

  function resetVoiceState() {
    vs.currentContentRole = null;
    vs.userTranscriptBuffer = "";
    vs.userSpeechStartMs = 0;
    vs.userSpeechEndMs = 0;
    vs.assistantEndMs = 0;
    vs.awaitingFirstResponse = false;
    // NOTE: errorRestartCount is NOT reset here — it must accumulate across
    // consecutive error-restart attempts. Only reset on successful audioOutput.
  }

  // Handle voice session errors - only auto-restart if voice is actively being used
  socket.on("error", (data) => {
    console.warn("[socketStore] Server error:", data);
    const { isVoiceActive } = get();
    if (!isVoiceActive) {
      console.log("[socketStore] Voice not active, skipping auto-restart");
      return;
    }

    const reason = classifyVoiceError(data);

    // Fatal errors (e.g. credential failure) — stop voice entirely, don't retry
    const isFatal = data.source === "bidirectionalStream" ||
                    data.message?.includes("Failed to start session") ||
                    data.details?.includes("prompts were not closed") ||
                    data.details?.includes("unclosed contents") ||
                    data.details?.includes("No open prompt") ||
                    data.details?.includes("No open content") ||
                    data.details?.includes("Duplicate prompt");
    if (isFatal) {
      console.error("[socketStore] Fatal voice session error, stopping voice");
      resetVoiceState();
      minervaService.recordAppEvent(AppEvent.VOICE_SESSION_ERROR);
      const { audioProcessor, audioPlayer } = useAudioStore.getState();
      audioProcessor?.stop();
      audioPlayer?.stop();
      set({ isVoiceActive: false });
      useAudioStore.getState().setStreaming(false);
      useFaceStore.getState().resetAll(); // fatal voice error
      trackErrorToast("Muse", `fatal_stream_error.${reason}`);
      useErrorStore.getState().addError(VOICE_ERROR, "error");
      return;
    }

    // Restart on timeout only — protocol errors are fatal (handled above)
    const shouldRestart = data.details?.includes("Timed out") ||
                         data.details?.includes("55 seconds") ||
                         data.source === "responseStream";
    if (shouldRestart) {
      // Exponential backoff: 1s, 2s, 3s — gives HTTP/2 connection time to drain
      const retryDelayMs = (errorRestartCount + 1) * 1000;
      console.log("[socketStore] Voice session error while active, scheduling restart (delay %dms)...", retryDelayMs);
      const errorGeneration = getVoiceGeneration();
      const timerId = setTimeout(() => {
        setAutoRestartTimer(null);
        if (isVoiceLocked()) return;
        if (getVoiceGeneration() !== errorGeneration) {
          console.log("[socketStore] Skipping error auto-restart (generation mismatch)");
          return;
        }
        const { isConnected, isVoiceActive: stillActive, storyLoading } = get();
        if (storyLoading) {
          console.log("[socketStore] Skipping error auto-restart (storyLoading)");
          return;
        }
        if (!isConnected || !stillActive) return;

        errorRestartCount++;
        if (errorRestartCount > MAX_ERROR_RESTARTS) {
          console.error("[socketStore] Max error restarts reached (%d), stopping voice", MAX_ERROR_RESTARTS);
          resetVoiceState();
          const { audioProcessor, audioPlayer } = useAudioStore.getState();
          audioProcessor?.stop();
          audioPlayer?.stop();
          set({ isVoiceActive: false });
          useAudioStore.getState().setStreaming(false);
          useFaceStore.getState().resetAll();
          trackErrorToast("Muse", `max_error_restarts.${reason}`);
          useErrorStore.getState().addError(VOICE_ERROR, "error");
          return;
        }
        console.log("[socketStore] Error auto-restart attempt %d/%d", errorRestartCount, MAX_ERROR_RESTARTS);
        resetVoiceState();
        useSocketStore.getState().startVoice({ resume: true }).catch((err) =>
          console.warn("[socketStore] error auto-restart failed:", err)
        );
      }, retryDelayMs);
      // Register so stopVoice/startVoice can cancel via cancelPendingAutoRestart()
      setAutoRestartTimer(timerId);
    }
  });

  socket.on("contentStart", (data) => {
    if (data?.type === "AUDIO") {
      const { audioPlayer } = useAudioStore.getState();
      if (audioPlayer && data.audioOutputConfiguration) {
        audioPlayer.configure(data.audioOutputConfiguration);
      }
    } else if (data?.type === "TEXT") {
      vs.currentContentRole = data.role || null;
      if (data.role === "ASSISTANT" && !useChatStore.getState().currentAiBuffer) {
        set({ transcriptText: "" });
        useChatStore.getState().startAiTurn();
      } else if (data.role === "USER") {
        vs.userTranscriptBuffer = "";
        vs.userSpeechStartMs = Date.now();
        vs.awaitingFirstResponse = false;
      }
    }
  });

  socket.on("textOutput", (data) => {
    if (!data.content) return;
    const role = data.role || vs.currentContentRole;
    if (role === "USER") {
      vs.userTranscriptBuffer += data.content;
    } else if (role === "ASSISTANT") {
      // Filter out Nova Sonic metadata like {"interrupted":true}
      const trimmed = data.content.trim();
      if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed.interrupted !== undefined) return;
        } catch { /* not JSON, continue */ }
      }
      set((s) => ({ transcriptText: (s as any).transcriptText + data.content }));
      useChatStore.getState().appendAiChunk(data.content);
    }
  });

  socket.on("audioOutput", (data) => {
    if (!data.content) return;
    const { isVoiceActive } = get();
    if (!isVoiceActive) return;
    // Session is working — reset error restart counter
    errorRestartCount = 0;
    if (vs.awaitingFirstResponse && vs.userSpeechEndMs > 0) {
      trackMuseLatency(Muse.ClientTimeToFirstResponse, Date.now() - vs.userSpeechEndMs);
      vs.awaitingFirstResponse = false;
    }
    const { audioPlayer } = useAudioStore.getState();
    if (!audioPlayer) return;
    try {
      audioPlayer.playAudio(decodeBase64Audio(data.content));
    } catch (error) {
      console.error("[socketStore] Error processing audio data:", error);
    }
  });

  socket.on("contentEnd", (data) => {
    if (data?.type === "TEXT") {
      if (vs.currentContentRole === "USER") {
        if (vs.userTranscriptBuffer) {
          useChatStore.getState().addMessage({ role: "user", source: "voice", content: vs.userTranscriptBuffer });
          vs.userTranscriptBuffer = "";
        }
        vs.userSpeechEndMs = Date.now();
        if (vs.assistantEndMs > 0) {
          trackMuseLatency(Muse.ClientUserResponseTime, vs.userSpeechEndMs - vs.assistantEndMs);
          vs.assistantEndMs = 0;
        }
        vs.awaitingFirstResponse = true;
        useFaceStore.getState().setAgentThinking(true); // contentEnd USER
      } else if (vs.currentContentRole === "ASSISTANT") {
        vs.assistantEndMs = Date.now();
        // Finalize each AI turn immediately so messages are interleaved
        // chronologically with user messages (not batched at streamComplete).
        useChatStore.getState().finalizeAiTurn();
      }
    }
    if (data?.stopReason?.toUpperCase() === "INTERRUPTED") {
      const { audioPlayer } = useAudioStore.getState();
      audioPlayer?.bargeIn();
    }
    vs.currentContentRole = null;
  });

  socket.on("streamComplete", () => {
    // If the kid is muted (stream alive, muteTimer ticking) and the server killed
    // the stream (renewal, disconnect), clear the timer so unmuteVoice doesn't try
    // to resume audio on a dead stream — it will fall through to full startVoice.
    if (clearMuteTimer()) {
      console.log("[socketStore] streamComplete: cleared muteTimer (stream died externally while muted)");
    }

    // Capture voice state BEFORE clearing the gate — if loadESSProject/stopVoice
    // set isVoiceActive=false, we must not auto-restart. Capturing BEFORE
    // clearStopGate ensures that startVoice (unblocked by the gate) can't flip
    // isVoiceActive back to true before we read it.
    const wasVoiceActive = get().isVoiceActive;

    // Unblock any startVoice waiting for the old session to finish tearing down.
    clearStopGate();
    console.log("[socketStore] streamComplete received (wasVoiceActive=%s, suppressed=%s)", wasVoiceActive, isSuppressed());
    // Capture the current voice generation — if a new session starts before
    // our auto-restart timer fires, the generation will have incremented and
    // we must skip the restart (it would create a duplicate session).
    const completedGeneration = getVoiceGeneration();

    useChatStore.getState().finalizeAiTurn();
    // Clear activity flags but keep voiceActive — session may auto-restart
    useFaceStore.getState().setAgentThinking(false);
    // Clear character focus after a delay so the highlight lingers briefly
    setTimeout(() => useStoryStore.getState().clearFocus(), 3000);

    if (!wasVoiceActive) {
      // Voice was already stopped by user (stopVoice/loadESSProject) — no auto-restart.
      // Only clear streaming state when we know voice won't restart.
      useAudioStore.getState().setStreaming(false);
      return;
    }

    // Primary defense: loadESSProject sets suppressAutoRestart=true. Any
    // streamComplete arriving during a project switch must not auto-restart.
    // The suppress flag clears 2s after startVoice (generation-aware), so
    // stream renewal (4 min later) is never blocked.
    if (isSuppressed()) {
      console.log("[socketStore] Skipping streamComplete auto-restart (suppressed — project switch in progress)");
      return;
    }

    // If a new session was JUST started (voiceLock still held from startVoice),
    // this streamComplete is from the OLD session and must not auto-restart.
    if (isVoiceLocked()) {
      console.log("[socketStore] Skipping streamComplete auto-restart (voiceLock held — new session already starting)");
      return;
    }


    // Voice is active and will auto-restart — do NOT set streaming=false here
    // to prevent a visual flicker (Muse orb going offline/online).

    // Voice was active when stream completed naturally — auto-restart
    // for continuous conversation. Keep isVoiceActive: true so that
    // stopVoice() called during the timeout window can cancel the restart.
    // Use cancelPendingAutoRestart() (called from stopVoice/startVoice) to
    // cancel this timer if a new session is started before it fires.
    const timerId = setTimeout(() => {
      setAutoRestartTimer(null); // clear reference
      if (isVoiceLocked()) return;
      // If a new voice session started since this streamComplete arrived,
      // skip auto-restart — the new session is already running and we'd create
      // a duplicate (the "session storm" bug).
      if (getVoiceGeneration() !== completedGeneration) {
        console.log("[socketStore] Skipping streamComplete auto-restart (generation mismatch: completed=%d, current=%d)", completedGeneration, getVoiceGeneration());
        return;
      }
      const { socket: currentSocket, isConnected, isVoiceActive: stillActive, storyLoading } = get();
      // Skip auto-restart if a project is loading — the voice session would be
      // built with stale/empty story context since restoreStory hasn't completed.
      // StoryBuilder's autoStartVoice will handle starting voice once load finishes.
      if (storyLoading) {
        console.log("[socketStore] Skipping streamComplete auto-restart (storyLoading)");
        return;
      }
      if (currentSocket && isConnected && stillActive) {
        // Reset handler-local state (currentContentRole, transcript buffer, metrics
        // timestamps) so the new session doesn't inherit stale state from the old one.
        resetVoiceState();
        // Use startVoice({ resume: true }) — preserves chat/transcript across
        // renewal. Cannot use restartVoice() (sends stopAudio → infinite loop).
        useSocketStore.getState().startVoice({ resume: true }).catch((err) =>
          console.warn("[socketStore] streamComplete auto-restart failed:", err)
        );
      } else {
        resetVoiceState();
        set({ isVoiceActive: false });
      }
    }, 300); // 300ms to allow concurrent stopVoice/startVoice to complete
    // Register the timer so external callers (stopVoice, startVoice, loadESSProject)
    // can cancel this restart if they're about to start their own session.
    setAutoRestartTimer(timerId);
  });
}
